import uniqWith from 'lodash/uniqWith';
import isEqual from 'lodash/isEqual';
import { Config } from '../video-data/config';
import type { AnimationCurveDescription } from './AnimationCurve';
import { AnimationCurve } from './AnimationCurve';

function isArrayInArray(array): array is [number, number][] {
    return Array.isArray(array[0]);
}

type AnimationCurveGroupDescription = {
    curve: AnimationCurveDescription;
    mode: string;
    percentageOffset: number;
    mirrorPercentageOffset: boolean;
    mirrorOffsetIncrement: boolean;
};

export class AnimationCurveGroup {
    classType = 'AnimCurveGroup';

    _percentage = 0;
    name = 'animCurveGroup_1';
    mode = 'single'; // multi
    animCurves: AnimationCurve[] = [];
    initPercentageOffset = 0.0; // initial percentage offset
    percentageOffset = 0.0; // incremental percentage offset
    mirrorPercentageOffset = false;
    mirrorOffsetIncrement = false;
    group: any;

    /**
     * @internal
     */
    _getDescription() {
        const description: AnimationCurveGroupDescription = {
            // NOTE: if we have multiple animation curves then we should only export the first one
            // since that's the one that's untouched during any manipulation process.
            curve: this.animCurves[0].getCurveDescription(),
            mode: this.mode,
            percentageOffset: this.percentageOffset,
            mirrorPercentageOffset: this.mirrorPercentageOffset,
            mirrorOffsetIncrement: this.mirrorOffsetIncrement,
        };

        if (this.animCurves.length > 1) {
            const drivenProperty = [];
            const drivenRange = [];
            const shouldOffsetPropertyValue = [];
            this.animCurves.forEach((curve) => {
                drivenProperty.push(curve._drivenProperty);
                drivenRange.push(curve._drivenRange);
                shouldOffsetPropertyValue.push(curve._shouldOffsetPropertyValue);
            });
            // collapse array down if ALL values are the same
            const colDrivenProperty = uniqWith(drivenProperty, isEqual);
            const colDrivenRange = uniqWith(drivenRange, isEqual);
            const colDhouldOffsetPropertyValue = uniqWith(shouldOffsetPropertyValue, isEqual);
            description.curve.drivenProperty =
                colDrivenProperty.length === 1 ? drivenProperty[0] : drivenProperty;
            description.curve.drivenRange =
                colDrivenRange.length === 1 ? drivenRange[0] : drivenRange;
            description.curve.shouldOffsetPropertyValue =
                colDhouldOffsetPropertyValue.length === 1
                    ? shouldOffsetPropertyValue[0]
                    : shouldOffsetPropertyValue;
        }

        return description;
    }

    smallestX(anchors = 'all') {
        return Math.min(
            ...this.animCurves
                .map((c) => c.smallestX(anchors))
                .filter((v) => typeof v === 'number'),
        );
    }

    largestX(anchors = 'all') {
        return Math.max(
            ...this.animCurves.map((c) => c.largestX(anchors)).filter((v) => typeof v === 'number'),
        );
    }

    scaleGroup(scaleFactor: number, anchors = Config.ALL_ANIMATION_CURVE_ANCHOR) {
        // we should always be scaling relative to the bounds
        // of the entire curve, which is why we have 'all' here
        const bounds = {
            smallestX: Math.min(this.smallestX(Config.ALL_ANIMATION_CURVE_ANCHOR), 0),
            largestX: Math.max(this.largestX(Config.ALL_ANIMATION_CURVE_ANCHOR), 1),
        };

        this.animCurves.forEach((c) => {
            c.scaleUsingBounds(scaleFactor, bounds, true, anchors);
        });
    }

    moveGroup(deltaX: number, deltaY: number, anchors = Config.ALL_ANIMATION_CURVE_ANCHOR) {
        this.animCurves.forEach((c) => {
            c.move(deltaX, deltaY, anchors);
        });
    }

    addStartPoint() {
        this.animCurves.forEach((c) => {
            c.addStartPoint();
        });
    }

    addEndPoint() {
        this.animCurves.forEach((c) => {
            c.addEndPoint();
        });
    }

    set percentage(value: number) {
        if (isNaN(value)) {
            throw Error(`${value} is not a valid value. Expected a number between 0 and 1.`);
        }
        this._percentage = value;
        this._evalPercentage();
    }

    get percentage(): number {
        return this._percentage;
    }

    _evalPercentage() {
        this.animCurves.forEach((animCurve) => {
            animCurve.percentage = this.percentage;
        });
    }

    hasAnchors(anchor = Config.START_ANIMATION_CURVE_ANCHOR) {
        return this.animCurves.some((c) => {
            return c.hasAnchors(anchor);
        });
    }

    /**
     * Reset all of the driven objects properties back to their curve
     * initialisation state. Note this is not the same as the objects creation
     * state.
     */
    reset() {
        this.animCurves.forEach((curve) => curve.reset());
    }

    clear() {
        this.animCurves.forEach((curve) => curve.clear());
        this.animCurves = [];
    }

    isEmpty(): boolean {
        return this.animCurves.length === 0;
    }

    destroy() {
        this.clear();
    }

    /**
     * Use 1 animation curve to drive multiple object properties
     **/
    static single(config) {
        const group = new AnimationCurveGroup();
        group.name = config.name || group.name;
        group.mode = 'single';
        group.initPercentageOffset = config.initPercentageOffset ?? 0;
        group.percentageOffset = config.percentageOffset ?? 0;
        group.mirrorPercentageOffset = config.mirrorPercentageOffset;

        const anim = AnimationCurve.fromDescription(config, config.drivenObjects);
        anim.group = group;
        anim.duration = config.duration;
        anim.move(group.initPercentageOffset, 0, Config.ALL_ANIMATION_CURVE_ANCHOR);
        group.animCurves.push(anim);

        return group;
    }

    /**
     * From 1 animation curve, create multiple, each with an offset to drive multiple object properties
     **/
    static multi(config) {
        const group = new AnimationCurveGroup();
        group.name = config.name || group.name;
        group.group = this;
        group.mode = 'multi';
        group.initPercentageOffset = config.initPercentageOffset ?? 0;
        group.percentageOffset = config.percentageOffset ?? 0;
        group.mirrorPercentageOffset = config.mirrorPercentageOffset;
        group.mirrorOffsetIncrement = config.mirrorOffsetIncrement;

        // apply offset config to curve using the given offset index
        const offsetCurve = (curve: AnimationCurve, offsetIndex: number) => {
            curve.index = offsetIndex;
            const curveOffsetStart =
                group.initPercentageOffset + group.percentageOffset * offsetIndex;
            let curveOffsetEnd = -(
                group.initPercentageOffset +
                group.percentageOffset * offsetIndex
            );
            if (group.mirrorOffsetIncrement) {
                curveOffsetEnd = -(
                    group.initPercentageOffset +
                    group.percentageOffset * (nDrivenObjects - offsetIndex)
                );
            }
            if (group.mirrorPercentageOffset) {
                curve.move(curveOffsetStart, 0, Config.START_ANIMATION_CURVE_ANCHOR);
                curve.move(curveOffsetEnd, 0, Config.END_ANIMATION_CURVE_ANCHOR);
            } else {
                curve.move(curveOffsetStart, 0);
            }
            curve.group = group;
            curve.duration = config.duration;
        };

        const nDrivenObjects = config.drivenObjects.length;
        for (let i = 0; i < nDrivenObjects; i++) {
            const driven = Array.isArray(config.drivenObjects[i])
                ? config.drivenObjects[i]
                : [config.drivenObjects[i]];

            // apply the offset to the entire object and all of its properties
            switch (config.offsetMode) {
                case 'object':
                    let multi = 1;
                    if (config.zigZagPerLine) {
                        if (config.zigZagPerLineStart === Config.ZIG_ZAG_START_RIGHT) {
                            multi = i % 2 ? 1 : -1;
                        } else {
                            multi = i % 2 ? -1 : 1;
                        }
                    }

                    let drivenRange;
                    if (isArrayInArray(config.drivenRange)) {
                        drivenRange = config.drivenRange.map((r) => [multi * r[0], multi * r[1]]);
                    } else {
                        drivenRange = [
                            multi * config.drivenRange[0],
                            multi * config.drivenRange[1],
                        ];
                    }
                    const objectConfig = Object.assign({}, config, { drivenRange });
                    delete objectConfig.drivenObjects;
                    const animForObject = AnimationCurve.fromDescription(objectConfig, driven);
                    offsetCurve(animForObject, i);
                    group.animCurves.push(animForObject);
                    break;
                case 'property':
                    const nDrivenProperties = config.drivenProperty.length;
                    for (let j = 0; j < nDrivenProperties; j++) {
                        const propertyConfig = Object.assign({}, config, {
                            drivenProperty: [config.drivenProperty[j]],
                        });
                        delete propertyConfig.drivenObjects;
                        const anim = AnimationCurve.fromDescription(propertyConfig, driven);
                        offsetCurve(anim, i + j);
                        group.animCurves.push(anim);
                    }
                    break;
                default:
                    throw Error(`Unknown offsetMode ${config.offsetMode}`);
            }
        }

        return group;
    }
}
